import React, { useEffect, useState } from 'react';

/**
 * Modal profesional para notificar al usuario el resultado del envío de solicitud.
 * 
 * Props:
 * - open: boolean
 * - type: 'success' | 'error' | 'loading'
 * - title: string (opcional)
 * - message: string
 * - onClose: () => void
 * - autoCloseMs: number (opcional, auto-cierra después de N ms, solo para success)
 */

const SubmitNotificationModal = ({ open, type = 'success', title, message, onClose, autoCloseMs = 0 }) => {
  const [progress, setProgress] = useState(100);

  // Auto-close timer con barra de progreso
  useEffect(() => {
    if (!open || type === 'loading' || !autoCloseMs) {
      setProgress(100);
      return;
    }

    const interval = 50; // actualizar cada 50ms
    const steps = autoCloseMs / interval;
    let current = 0;

    const timer = setInterval(() => {
      current++;
      setProgress(100 - (current / steps) * 100);
      if (current >= steps) {
        clearInterval(timer);
        onClose();
      }
    }, interval);

    return () => clearInterval(timer);
  }, [open, type, autoCloseMs, onClose]);

  if (!open) return null;

  const config = {
    success: {
      icon: (
        <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
          <circle cx="32" cy="32" r="30" fill="#e8f5e9" stroke="#43a047" strokeWidth="2"/>
          <path d="M20 33l8 8 16-18" stroke="#43a047" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" fill="none">
            <animate attributeName="stroke-dasharray" from="0 50" to="50 0" dur="0.5s" fill="freeze"/>
          </path>
        </svg>
      ),
      accentColor: '#43a047',
      bgGradient: 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)',
      defaultTitle: '¡Solicitud enviada!',
    },
    error: {
      icon: (
        <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
          <circle cx="32" cy="32" r="30" fill="#ffebee" stroke="#e53935" strokeWidth="2"/>
          <path d="M22 22l20 20M42 22l-20 20" stroke="#e53935" strokeWidth="3.5" strokeLinecap="round">
            <animate attributeName="stroke-dasharray" from="0 30" to="30 0" dur="0.4s" fill="freeze"/>
          </path>
        </svg>
      ),
      accentColor: '#e53935',
      bgGradient: 'linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)',
      defaultTitle: 'Error al enviar',
    },
    loading: {
      icon: (
        <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
          <circle cx="32" cy="32" r="28" stroke="#e0e0e0" strokeWidth="4"/>
          <circle cx="32" cy="32" r="28" stroke="#5b6abf" strokeWidth="4" strokeLinecap="round"
            strokeDasharray="120 60"
          >
            <animateTransform attributeName="transform" type="rotate" from="0 32 32" to="360 32 32" dur="1s" repeatCount="indefinite"/>
          </circle>
        </svg>
      ),
      accentColor: '#5b6abf',
      bgGradient: 'linear-gradient(135deg, #e8eaf6 0%, #c5cae9 100%)',
      defaultTitle: 'Enviando solicitud...',
    },
  };

  const c = config[type] || config.success;

  return (
    <>
      <style>{`
        @keyframes notifModalIn {
          from { opacity: 0; transform: scale(0.85) translateY(20px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
        @keyframes notifOverlayIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes notifPulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
      `}</style>
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(0,0,0,0.4)',
          zIndex: 2000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          animation: 'notifOverlayIn 0.2s ease-out',
        }}
        onClick={type !== 'loading' ? onClose : undefined}
      >
        <div
          style={{
            background: '#fff',
            borderRadius: 20,
            boxShadow: '0 24px 80px rgba(0,0,0,0.18)',
            maxWidth: 420,
            width: '92%',
            overflow: 'hidden',
            animation: 'notifModalIn 0.35s cubic-bezier(.4,0,.2,1)',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Accent bar */}
          <div style={{
            height: 5,
            background: c.accentColor,
            borderRadius: '20px 20px 0 0',
          }} />

          {/* Content */}
          <div style={{
            padding: '36px 32px 28px',
            textAlign: 'center',
          }}>
            {/* Icon */}
            <div style={{
              marginBottom: 20,
              display: 'flex',
              justifyContent: 'center',
              animation: type === 'loading' ? 'notifPulse 2s ease-in-out infinite' : 'none',
            }}>
              {c.icon}
            </div>

            {/* Title */}
            <h3 style={{
              margin: '0 0 12px',
              fontSize: 22,
              fontWeight: 800,
              color: '#1a1a2e',
              letterSpacing: '-0.3px',
            }}>
              {title || c.defaultTitle}
            </h3>

            {/* Message */}
            <p style={{
              margin: '0 0 24px',
              fontSize: 15,
              color: '#555',
              lineHeight: 1.6,
              whiteSpace: 'pre-line',
            }}>
              {message}
            </p>

            {/* Auto-close progress bar */}
            {autoCloseMs > 0 && type !== 'loading' && (
              <div style={{
                width: '100%',
                height: 4,
                background: '#eee',
                borderRadius: 4,
                marginBottom: 20,
                overflow: 'hidden',
              }}>
                <div style={{
                  width: `${progress}%`,
                  height: '100%',
                  background: c.accentColor,
                  borderRadius: 4,
                  transition: 'width 50ms linear',
                }} />
              </div>
            )}

            {/* Action button */}
            {type !== 'loading' && (
              <button
                onClick={onClose}
                style={{
                  background: c.accentColor,
                  color: '#fff',
                  border: 'none',
                  borderRadius: 12,
                  padding: '14px 48px',
                  fontSize: 15,
                  fontWeight: 700,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  boxShadow: `0 4px 16px ${c.accentColor}40`,
                  letterSpacing: '0.3px',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = `0 8px 24px ${c.accentColor}50`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = `0 4px 16px ${c.accentColor}40`;
                }}
              >
                {type === 'success' ? 'Entendido' : 'Cerrar'}
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default SubmitNotificationModal;
